import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, ChevronDown, ChevronUp, Coffee, Salad, Utensils, Cookie, Coffee as Beverage } from 'lucide-react';

/**
 * Preview Recipes Page
 * Read-only version showing actual recipe structure with mock interaction
 */

// Sample preview recipes (subset of actual recipes for demo)
const previewRecipes = [
  {
    id: 'brown-rice-porridge',
    name: 'Brown Rice Porridge with Cooked Apples',
    type: 'Breakfast',
    whyChosen: 'Often included in macrobiotic and whole-grain dietary patterns. Uses gentle cooking methods common in traditional approaches.',
    ingredients: [
      'Cooked brown rice',
      'Water or unsweetened plant milk',
      'Apple, diced',
      'Cinnamon (optional)',
      'Pinch of sea salt'
    ],
    preparation: [
      'Add cooked brown rice to a pot with water or plant milk (1:2 ratio)',
      'Bring to a gentle simmer over medium-low heat',
      'Add diced apple and a pinch of salt',
      'Cook for 10-15 minutes, stirring occasionally, until creamy',
      'Add cinnamon if desired',
      'Serve warm'
    ]
  },
  {
    id: 'miso-soup',
    name: 'Miso Soup with Tofu and Greens',
    type: 'Lunch',
    whyChosen: 'Central to macrobiotic dietary tradition. Uses fermented foods and leafy greens common in traditional Japanese dietary patterns.',
    ingredients: [
      'Water',
      'Wakame seaweed (small piece)',
      'Tofu, cubed',
      'Leafy greens (spinach, kale, or watercress)',
      'Miso paste (1-2 tablespoons per bowl)',
      'Green onions, sliced (optional)'
    ],
    preparation: [
      'Soak wakame in warm water for 5 minutes, then chop',
      'Bring water to a gentle simmer',
      'Add wakame and simmer for 5 minutes',
      'Add tofu cubes and greens',
      'Remove from heat and dissolve miso paste'
    ]
  },
  {
    id: 'steamed-fish-bowl',
    name: 'Steamed Fish with Greens and Brown Rice',
    type: 'Dinner',
    whyChosen: 'Uses gentle cooking methods common in macrobiotic and traditional dietary patterns. Combines protein with whole grains and greens.',
    ingredients: [
      'White fish (cod, halibut, or flounder)',
      'Brown rice, cooked',
      'Leafy greens (kale or spinach)',
      'Lemon',
      'Ginger, sliced (optional)'
    ],
    preparation: [
      'Season fish lightly with salt',
      'Steam for 8-12 minutes until fish flakes easily',
      'Steam greens separately until just wilted',
      'Serve fish over brown rice with greens'
    ]
  },
  {
    id: 'steamed-kale-sesame',
    name: 'Steamed Kale with Sesame',
    type: 'Snack',
    whyChosen: 'Simple preparation common in macrobiotic dietary patterns. Uses leafy greens and sesame seeds.',
    ingredients: [
      'Kale',
      'Sesame seeds',
      'Tamari or sea salt',
      'Lemon juice (optional)'
    ],
    preparation: [
      'Wash kale and remove thick stems',
      'Steam for 3-5 minutes until just tender',
      'Toss with toasted sesame seeds',
      'Season with tamari or salt'
    ]
  },
  {
    id: 'green-tea',
    name: 'Green Tea (Sencha / Matcha)',
    type: 'Beverage',
    whyChosen: 'Common in Asian dietary traditions and often discussed in integrative approaches.',
    ingredients: [
      'Green tea leaves (sencha) or matcha powder',
      'Hot water (not boiling)'
    ],
    preparation: [
      'Heat water to 70-80°C (160-175°F)',
      'For sencha: steep 1 teaspoon leaves for 1-2 minutes',
      'For matcha: whisk 1/2 teaspoon powder with hot water',
      'Serve immediately'
    ]
  }
];

const recipeTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Beverage'];

const getTypeIcon = (type) => {
  switch (type) {
    case 'Breakfast': return Coffee;
    case 'Lunch': return Salad;
    case 'Dinner': return Utensils;
    case 'Snack': return Cookie;
    case 'Beverage': return Beverage;
    default: return Utensils;
  }
};

const PreviewRecipeCard = ({ recipe }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = getTypeIcon(recipe.type);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-50 transition-colors rounded-xl"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Icon className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-blue-600">
              {recipe.type}
            </span>
          </div>
          <h3 className="text-xl font-semibold text-slate-900">
            {recipe.name}
          </h3>
        </div>
        <div className="ml-4">
          {isExpanded ? (
            <ChevronUp className="h-6 w-6 text-slate-400" />
          ) : (
            <ChevronDown className="h-6 w-6 text-slate-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 space-y-6 border-t border-slate-100 pt-6">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              Why People Choose This Recipe
            </h4>
            <p className="text-slate-700 leading-relaxed">
              {recipe.whyChosen}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              Ingredients
            </h4>
            <ul className="space-y-2">
              {recipe.ingredients.map((ingredient, index) => (
                <li key={index} className="flex items-start text-slate-700">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span>{ingredient}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
              Preparation
            </h4>
            <ol className="space-y-3">
              {recipe.preparation.map((step, index) => (
                <li key={index} className="flex items-start text-slate-700">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                    {index + 1}
                  </span>
                  <span className="flex-1">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs text-slate-700 leading-relaxed">
              This recipe is provided for general educational purposes only and is not intended as medical advice.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const PreviewRecipesPage = () => {
  const [selectedType, setSelectedType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRecipes = previewRecipes.filter(recipe => {
    const matchesType = selectedType === 'All' || recipe.type === selectedType;
    const matchesSearch = recipe.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  return (
    <div className="space-y-8">
      {/* Back Button */}
      <Link
        to="/preview/integrative-health"
        className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Integrative Health
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
          Recipes (Supportive)
        </h1>
        <p className="text-lg text-slate-700 leading-relaxed mb-6">
          Simple, whole-food recipes often chosen by people seeking plant-forward, anti-inflammatory eating patterns.
        </p>
      </div>

      {/* Global Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <p className="text-sm text-slate-700 leading-relaxed">
          This information is provided for educational purposes only and does not replace professional medical advice. Always consult your healthcare provider before making dietary, supplement, or lifestyle changes, especially if you are immunocompromised, have low blood counts, or are undergoing treatment.
        </p>
      </div>

      {/* Recipe Library */}
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">
          Recipe Library
        </h2>

        {/* Filters */}
        <div className="space-y-4 mb-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Type Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setSelectedType('All')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                selectedType === 'All'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All Recipes
            </button>
            {recipeTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  selectedType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <p className="text-sm text-slate-600 mb-4">
          Showing {filteredRecipes.length} of 5 preview recipes (full library available in app)
        </p>

        {/* Recipe Cards */}
        <div className="space-y-4">
          {filteredRecipes.length > 0 ? (
            filteredRecipes.map((recipe) => (
              <PreviewRecipeCard key={recipe.id} recipe={recipe} />
            ))
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-600">No recipes match your search.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewRecipesPage;
